<script lang="ts">
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import { link } from 'svelte-spa-router';
  import { api } from '../../framework/js/api.js';
  import { GUEST_TRIAL_STORAGE_KEY, GUEST_GAPFINDER_DRAFT_KEY } from '../../js/guestTrialProducts';
  import {
    saveGuestGapfinderFile,
    loadGuestGapfinderFile,
    clearGuestGapfinderFile,
  } from '../../js/guestGapfinderFileStore';

  const MAX_FILE_BYTES = 5 * 1024 * 1024;
  const ALLOWED_EXTENSIONS = new Set(['pdf', 'doc', 'docx']);

  type InputMode = 'upload' | 'paste';

  interface GuestGapfinderDraft {
    mode: InputMode;
    title: string;
    content?: string;
    fileName?: string;
    validated?: boolean;
  }

  let mode: InputMode = 'upload';
  let pastedText = '';
  let title = '';
  let file: File | null = null;
  let showLoginWall = false;
  let isDragging = false;
  let validating = false;
  let fieldError = '';

  function persistDraftMeta(extra: Partial<GuestGapfinderDraft> = {}) {
    try {
      const draft: GuestGapfinderDraft = {
        mode,
        title,
        validated: extra.validated ?? canProceed,
        ...(mode === 'paste' ? { content: pastedText } : {}),
        ...(mode === 'upload' && file ? { fileName: file.name } : {}),
        ...extra,
      };
      localStorage.setItem(GUEST_GAPFINDER_DRAFT_KEY, JSON.stringify(draft));
      localStorage.setItem(GUEST_TRIAL_STORAGE_KEY, 'gapfinder');
    } catch (_) {}
  }

  async function loadDraft() {
    try {
      const raw = localStorage.getItem(GUEST_GAPFINDER_DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as GuestGapfinderDraft;
      mode = parsed.mode === 'paste' ? 'paste' : 'upload';
      title = parsed.title || '';
      pastedText = parsed.content || '';
      if (mode === 'upload' && parsed.fileName) {
        const stored = await loadGuestGapfinderFile();
        if (stored) file = stored;
      }
    } catch (_) {}
  }

  onMount(() => {
    localStorage.setItem(GUEST_TRIAL_STORAGE_KEY, 'gapfinder');
    void loadDraft();
  });

  function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  function extensionOf(name: string): string {
    const parts = name.split('.');
    return parts.length > 1 ? (parts.pop() || '').toLowerCase() : '';
  }

  function validateFileClient(f: File): string | null {
    const ext = extensionOf(f.name);
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      return 'Only PDF and Word documents (.pdf, .doc, .docx) are allowed.';
    }
    if (f.size > MAX_FILE_BYTES) {
      return 'File must be 5 MB or smaller.';
    }
    return null;
  }

  async function validateWithServer(): Promise<boolean> {
    fieldError = '';
    validating = true;
    try {
      let res: any;
      if (mode === 'upload') {
        if (!file) return false;
        const clientErr = validateFileClient(file);
        if (clientErr) {
          fieldError = clientErr;
          return false;
        }
        const fd = new FormData();
        fd.append('file', file);
        res = await api.validateGuestGapfinder(fd);
      } else {
        const text = pastedText.trim();
        if (!text) {
          fieldError = 'Paste your brief text first.';
          return false;
        }
        res = await api.validateGuestGapfinder({ content: text });
      }

      if (!res?.success) {
        fieldError = res?.message || 'That does not look like a research brief.';
        return false;
      }
      return true;
    } catch (e) {
      console.error(e);
      fieldError = 'Could not validate your brief. Please try again.';
      return false;
    } finally {
      validating = false;
    }
  }

  async function setFile(next: File | null) {
    fieldError = '';
    if (!next) {
      file = null;
      await clearGuestGapfinderFile();
      persistDraftMeta({ validated: false });
      return;
    }

    const clientErr = validateFileClient(next);
    if (clientErr) {
      fieldError = clientErr;
      file = null;
      return;
    }

    file = next;
    const ok = await validateWithServer();
    if (!ok) {
      file = null;
      await clearGuestGapfinderFile();
      return;
    }

    await saveGuestGapfinderFile(next);
    pastedText = '';
    persistDraftMeta({ validated: true });
  }

  function onFileSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    const picked = input.files?.[0] ?? null;
    void setFile(picked);
    input.value = '';
  }

  function onDragOver(e: DragEvent) {
    e.preventDefault();
    isDragging = true;
  }

  function onDragLeave() {
    isDragging = false;
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    isDragging = false;
    const dropped = e.dataTransfer?.files?.[0] ?? null;
    void setFile(dropped);
  }

  function onPasteInput(e: Event) {
    pastedText = (e.target as HTMLTextAreaElement).value;
    fieldError = '';
    persistDraftMeta({ validated: false });
  }

  function onTitleInput(e: Event) {
    title = (e.target as HTMLInputElement).value;
    persistDraftMeta();
  }

  function switchMode(next: InputMode) {
    if (mode === next) return;
    mode = next;
    fieldError = '';
    if (next === 'paste') {
      void setFile(null);
    } else {
      pastedText = '';
    }
    persistDraftMeta({ validated: false });
  }

  $: canProceed =
    mode === 'upload' ? !!file : pastedText.trim().length > 0;

  async function analyse() {
    if (!canProceed || validating) return;
    const ok = mode === 'upload' && file ? true : await validateWithServer();
    if (!ok) return;

    if (mode === 'upload' && file) {
      await saveGuestGapfinderFile(file);
      persistDraftMeta({ validated: true, fileName: file.name });
    } else {
      await clearGuestGapfinderFile();
      persistDraftMeta({ validated: true, content: pastedText });
    }

    showLoginWall = true;
  }

  function goBack() {
    if (showLoginWall) {
      showLoginWall = false;
      return;
    }
    window.location.href = '/#/guest-brief-new';
  }
</script>

<div class="w640 wizard-wrap">
  {#if showLoginWall}
    <div class="login-wall" in:fade={{ duration: 180 }}>
      <div class="ui icon message">
        <i class="info circle icon" aria-hidden="true"></i>
        <div class="content">
          <p>Please log in or register to run your GapFinder analysis.</p>
        </div>
      </div>
      <div class="flow-center login-actions">
        <a use:link href="/login" class="ui primary button">Log in</a>
        <a use:link href="/signup" class="ui green button">Register</a>
      </div>
      <button type="button" class="back-link" on:click={goBack}>
        <i class="arrow left icon" aria-hidden="true"></i>
        Back to your brief
      </button>
    </div>
  {:else}
    <div class="wizard-header">
      <span class="wizard-eyebrow wizard-eyebrow--pink">
        <i class="search icon" aria-hidden="true"></i>
        GapFinder
      </span>
    </div>

    <header class="step-header">
      <h1 class="step-title">Upload or paste your brief</h1>
      <p class="step-sub">
        Add a PDF or Word document (max 5 MB) or paste the text below. We'll stress-test it across 12 dimensions once you sign in.
      </p>
    </header>

    <div class="segment" role="tablist" aria-label="Brief source">
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'upload'}
        class="seg-btn"
        class:active={mode === 'upload'}
        on:click={() => switchMode('upload')}
      >
        <i class="cloud upload icon" aria-hidden="true"></i>
        <span>Upload</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'paste'}
        class="seg-btn"
        class:active={mode === 'paste'}
        on:click={() => switchMode('paste')}
      >
        <i class="paste icon" aria-hidden="true"></i>
        <span>Paste text</span>
      </button>
    </div>

    <div class="field-area">
      <div class="ui form">
        <div class="field">
          <label for="gapfinder-title" class="input-label">Title <span class="optional">(optional)</span></label>
          <input
            id="gapfinder-title"
            type="text"
            value={title}
            on:input={onTitleInput}
            placeholder="e.g., Q3 brand tracking brief"
            maxlength="120"
            class="text-input input_bg"
          />
        </div>

        {#if mode === 'upload'}
          {#if file}
            <div class="file-selected" in:fade={{ duration: 120 }}>
              <div class="file-icon-chip" aria-hidden="true">
                <i class="file pdf outline icon"></i>
              </div>
              <div class="file-meta">
                <div class="file-name">{file.name}</div>
                <div class="file-size">{formatBytes(file.size)}</div>
              </div>
              <button
                type="button"
                class="change-link"
                on:click={() => void setFile(null)}
                aria-label="Remove selected file"
              >
                <i class="times icon" aria-hidden="true"></i>
              </button>
            </div>
          {:else}
            <!-- svelte-ignore a11y-no-static-element-interactions -->
            <label
              class="dropzone"
              class:dragging={isDragging}
              on:dragover={onDragOver}
              on:dragleave={onDragLeave}
              on:drop={onDrop}
            >
              <input
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                on:change={onFileSelected}
                class="file-input"
                aria-label="Upload brief file"
              />
              <div class="dz-icon-chip" aria-hidden="true">
                <i class="cloud upload icon"></i>
              </div>
              <div class="dz-text">
                <strong>Drop your brief here</strong>
                <span>or <em>click to browse</em></span>
              </div>
              <div class="dz-formats">PDF · DOC · DOCX · max 5 MB</div>
            </label>
          {/if}
        {:else}
          <div class="field">
            <label for="gapfinder-paste" class="input-label">Brief text</label>
            <textarea
              id="gapfinder-paste"
              value={pastedText}
              on:input={onPasteInput}
              placeholder="Paste your brief here…"
              rows="10"
              class="paste-area input_bg resize-none"
            ></textarea>
          </div>
        {/if}

        {#if fieldError}
          <p class="field-error" role="alert">{fieldError}</p>
        {/if}
        {#if validating}
          <p class="field-hint" aria-live="polite">
            <i class="spinner loading icon" aria-hidden="true"></i>
            Checking your brief…
          </p>
        {/if}
      </div>
    </div>

    <div class="nav-row">
      <button type="button" class="text-back" on:click={goBack}>
        <i class="arrow left icon" aria-hidden="true"></i>
        Choose another product
      </button>
      <button
        type="button"
        class="ui primary button analyse-btn"
        on:click={analyse}
        disabled={!canProceed || validating}
      >
        {validating ? 'Checking…' : 'Analyse brief'}
        <i class="angle right icon" aria-hidden="true"></i>
      </button>
    </div>
  {/if}
</div>

<style>
  .wizard-wrap {
    margin: 0 auto;
    padding: calc(4.5rem + var(--space-6) + 20px) var(--space-6) var(--space-8);
    min-height: 100vh;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  .wizard-header {
    display: flex;
    justify-content: center;
    margin-bottom: var(--space-4);
  }

  .wizard-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    border-radius: 999px;
    letter-spacing: 0.02em;
  }

  .wizard-eyebrow--pink {
    color: var(--accent-pink-ink);
    background: var(--accent-pink-soft);
  }

  .step-header {
    margin-bottom: var(--space-5);
  }

  .step-title {
    margin: 0 0 var(--space-2);
    font-size: var(--text-2xl);
    font-weight: 600;
    color: var(--grey);
  }

  .step-sub {
    margin: 0;
    color: var(--grey-secondary);
    line-height: 1.6;
  }

  .segment {
    display: inline-flex;
    gap: 2px;
    padding: 3px;
    margin-bottom: var(--space-5);
    background: var(--surface-sunken);
    border-radius: var(--radius-input);
    border: 1px solid var(--border-color);
  }

  .seg-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-4);
    border: 0;
    border-radius: calc(var(--radius-input) - 2px);
    background: transparent;
    color: var(--grey-secondary);
    font-size: var(--text-sm);
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  }

  .seg-btn :global(i.icon) {
    margin: 0;
  }

  .seg-btn:hover:not(.active) {
    color: var(--grey);
    background: rgba(0, 0, 0, 0.02);
  }

  .seg-btn.active {
    background: var(--surface);
    color: var(--primary);
    box-shadow: var(--shadow-card);
  }

  .seg-btn:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .field-area {
    margin-bottom: var(--space-5);
  }

  .input-label {
    display: block;
    margin-bottom: var(--space-2);
    font-weight: 400;
    color: var(--grey);
  }

  .optional {
    color: var(--grey-secondary);
    font-weight: 400;
  }

  .text-input,
  .paste-area {
    width: 100%;
    box-sizing: border-box;
    padding: var(--space-3);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-input);
    font: inherit;
    color: var(--grey);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .paste-area {
    min-height: 10rem;
    line-height: 1.5;
  }

  .text-input:focus,
  .paste-area:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  .dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--space-8) var(--space-5);
    border: 2px dashed var(--border-strong);
    border-radius: var(--radius-card);
    background: var(--surface-sunken);
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease;
    margin-top: var(--space-4);
  }

  .dropzone:hover,
  .dropzone.dragging {
    border-color: var(--primary);
    background: color-mix(in srgb, var(--primary) 6%, var(--surface-sunken));
  }

  .file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }

  .dz-icon-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    margin-bottom: var(--space-3);
    border-radius: var(--radius-md);
    background: var(--accent-pink-soft);
    color: var(--accent-pink-ink);
  }

  .dz-text {
    text-align: center;
    color: var(--grey);
    line-height: 1.5;
  }

  .dz-text span {
    color: var(--grey-secondary);
  }

  .dz-formats {
    margin-top: var(--space-2);
    font-size: var(--text-sm);
    color: var(--grey-secondary);
  }

  .file-selected {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-top: var(--space-4);
    padding: var(--space-4);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    background: var(--surface);
  }

  .file-icon-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: var(--radius-md);
    background: var(--accent-pink-soft);
    color: var(--accent-pink-ink);
    flex-shrink: 0;
  }

  .file-meta {
    flex: 1;
    min-width: 0;
  }

  .file-name {
    font-weight: 500;
    color: var(--grey);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-size {
    font-size: var(--text-sm);
    color: var(--grey-secondary);
  }

  .change-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--grey-secondary);
    cursor: pointer;
  }

  .change-link:hover {
    background: var(--surface-sunken);
    color: var(--grey);
  }

  .change-link:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .field-error {
    margin: var(--space-3) 0 0;
    font-size: var(--text-sm);
    color: var(--status-danger-ink);
  }

  .field-hint {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin: var(--space-3) 0 0;
    font-size: var(--text-sm);
    color: var(--grey-secondary);
  }

  .nav-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .text-back {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: 0;
    border: 0;
    background: none;
    color: var(--grey-secondary);
    cursor: pointer;
    font: inherit;
  }

  .text-back:hover {
    color: var(--primary);
  }

  .text-back:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
    border-radius: var(--radius-sm);
  }

  .analyse-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }

  .analyse-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .login-wall {
    text-align: center;
    padding: var(--space-6) 0;
  }

  .login-actions {
    gap: var(--space-3);
    margin: var(--space-5) 0;
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    margin-top: var(--space-4);
    padding: 0;
    border: 0;
    background: none;
    color: var(--grey-secondary);
    cursor: pointer;
    font: inherit;
  }

  .back-link:hover {
    color: var(--primary);
  }

  .back-link:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
    border-radius: var(--radius-sm);
  }

  @media (max-width: 768px) {
    .wizard-wrap {
      padding: calc(4rem + var(--space-4) + 20px) var(--space-4) var(--space-6);
    }
  }
</style>
